import { Box, Typography, Card, CardContent, CardActions, Button, CircularProgress, IconButton, Grid } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../../hooks/useProjects';
import { ROUTES } from '../../config/constants';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import { useState } from 'react';
import { PodcastDiscoveryTutorial } from '../../components/onboarding/PodcastDiscoveryTutorial';
import { usePodcastDiscoveryTutorial } from '../../hooks/usePodcastDiscoveryTutorial';

export const ProjectList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('projects');
  const { projects, isLoading, deleteProject, isDeleting } = useProjects();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  
  // Calculate total episode count across all projects
  const totalEpisodeCount = projects?.reduce((sum, project) => sum + (project.podcastEpisodes?.length || 0), 0) || 0;
  
  // Debug logging
  console.log('🎯 Tutorial Debug:', {
    projectCount: projects?.length || 0,
    totalEpisodeCount,
    projects: projects,
  });
  
  // Podcast discovery tutorial for first project with no episodes
  const { 
    showTutorial: showPodcastTutorial, 
    completeTutorial: completePodcastTutorial, 
    skipTutorial: skipPodcastTutorial 
  } = usePodcastDiscoveryTutorial({ projectCount: projects?.length || 0, episodeCount: totalEpisodeCount });

  const handleDelete = async (projectId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm(t('deleteConfirm'))) {
      setDeletingId(projectId);
      try {
        await deleteProject(projectId);
      } finally {
        setDeletingId(null);
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Podcast Discovery Tutorial - shows after first project */}
      <PodcastDiscoveryTutorial
        open={showPodcastTutorial}
        onComplete={completePodcastTutorial}
        onSkip={skipPodcastTutorial}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          {t('myProjects')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${ROUTES.PROJECTS}/new`)}
        >
          {t('createProject')}
        </Button>
      </Box>

      {projects.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
          textAlign="center"
        >
          <FolderIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="text.secondary">
            {t('noProjects')}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {t('createFirstProject')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`${ROUTES.PROJECTS}/new`)}
          >
            {t('createProject')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => navigate(`${ROUTES.PROJECTS}/${project.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2" noWrap>
                      {project.projectName}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('learning')}: {project.projectLanguage}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('userLanguage')}: {project.userLanguage}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('episodes')}: {project.podcastEpisodes?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('videos')}: {project.youtubeVideos?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    {t('lastAccessed')}: {new Date(project.lastAccessed).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`${ROUTES.PROJECTS}/${project.id}`); }}>
                      {t('open')}
                    </Button>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(project.id, e)}
                    disabled={isDeleting && deletingId === project.id}
                  >
                    {isDeleting && deletingId === project.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
